import { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import { XMarkIcon, PhotoIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';

import { supabase } from '@/lib/supabase';
import { Button, Spinner } from '@realvisor/ui';
import { ImageGalleryModal } from './ImageGalleryModal';

interface PropertyImage {
  id: string;
  url: string;
  name: string;
  size: number;
  uploadedAt: string;
}

interface PropertyImageUploadProps {
  leadId: string;
  images: PropertyImage[];
  onImagesChange: (images: PropertyImage[]) => void;
}

export function PropertyImageUpload({ leadId, images, onImagesChange }: PropertyImageUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Načíst existující obrázky ze Storage
  useEffect(() => {
    const loadImages = async () => {
      try {
        const { data, error } = await supabase.storage
          .from('property-images')
          .list(leadId, {
            limit: 100,
            offset: 0,
            sortBy: { column: 'created_at', order: 'desc' },
          });

        if (error) {
          console.error('Error loading images:', error);
          return;
        }

        if (data && data.length > 0) {
          const loadedImages: PropertyImage[] = data.map((file) => {
            const filePath = `${leadId}/${file.name}`;
            const { data: { publicUrl } } = supabase.storage
              .from('property-images')
              .getPublicUrl(filePath);

            return {
              id: filePath,
              url: publicUrl,
              name: file.name,
              size: (file.metadata as any)?.['size'] || 0,
              uploadedAt: file.created_at || new Date().toISOString(),
            };
          });

          onImagesChange(loadedImages);
        }
      } catch (error) {
        console.error('Error loading images:', error);
      } finally {
        setLoading(false);
      }
    };

    loadImages();
  }, [leadId, onImagesChange]);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;

    setUploading(true);
    const uploadedImages: PropertyImage[] = [];

    try {
      for (const file of acceptedFiles) {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          toast.error(`${file.name} není obrázek`);
          continue;
        }

        // Validate file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`${file.name} je příliš velký (max 10MB)`);
          continue;
        }

        // Generate unique filename
        const fileExt = file.name.split('.').pop();
        const fileName = `${leadId}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

        // Upload to Supabase Storage
        const { data, error } = await supabase.storage
          .from('property-images')
          .upload(fileName, file, {
            cacheControl: '3600',
            upsert: false,
          });

        if (error) {
          toast.error(`Chyba při nahrávání ${file.name}: ${error.message}`);
          continue;
        }

        // Get public URL
        const { data: { publicUrl } } = supabase.storage
          .from('property-images')
          .getPublicUrl(data.path);

        uploadedImages.push({
          id: data.path,
          url: publicUrl,
          name: file.name,
          size: file.size,
          uploadedAt: new Date().toISOString(),
        });
      }

      if (uploadedImages.length > 0) {
        onImagesChange([...images, ...uploadedImages]);
        toast.success(`Nahráno ${uploadedImages.length} obrázků`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Chyba při nahrávání obrázků');
    } finally {
      setUploading(false);
    }
  }, [leadId, images, onImagesChange]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'],
    },
    multiple: true,
    disabled: uploading,
  });

  const handleDelete = async (image: PropertyImage) => {
    if (!confirm(`Opravdu chcete smazat obrázek ${image.name}?`)) {
      return;
    }

    setDeletingId(image.id);
    try {
      const { error } = await supabase.storage
        .from('property-images')
        .remove([image.id]);

      if (error) {
        toast.error(`Chyba při mazání: ${error.message}`);
        return;
      }

      onImagesChange(images.filter((img) => img.id !== image.id));
      toast.success('Obrázek smazán');
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Chyba při mazání obrázku');
    } finally {
      setDeletingId(null);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-4">
      {/* Upload Area */}
      <div
        {...getRootProps()}
        className={`
          border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
          ${isDragActive
            ? 'border-primary-500 bg-primary-50'
            : 'border-gray-300 hover:border-gray-400 bg-gray-50'
          }
          ${uploading ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        <input {...getInputProps()} />
        <div className="flex flex-col items-center gap-2">
          {uploading ? (
            <>
              <Spinner size="lg" />
              <p className="text-sm text-gray-600">Nahrávám obrázky...</p>
            </>
          ) : (
            <>
              <ArrowUpTrayIcon className="h-12 w-12 text-gray-400" />
              <p className="text-sm font-medium text-gray-700">
                {isDragActive
                  ? 'Přetáhněte sem obrázky'
                  : 'Přetáhněte obrázky nebo klikněte pro výběr'}
              </p>
              <p className="text-xs text-gray-500">
                PNG, JPG, GIF, WEBP do 10MB
              </p>
            </>
          )}
        </div>
      </div>

      {/* Image Grid */}
      {images.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {images.map((image, index) => (
            <div
              key={image.id}
              className="relative group rounded-lg overflow-hidden border border-gray-200 bg-white"
            >
              <div 
                className="aspect-square cursor-pointer"
                onClick={() => {
                  setCurrentImageIndex(index);
                  setGalleryOpen(true);
                }}
              >
                <img
                  src={image.url}
                  alt={image.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all">
                <button
                  onClick={() => handleDelete(image)}
                  disabled={deletingId === image.id}
                  className="absolute top-2 right-2 p-1.5 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700 disabled:opacity-50"
                >
                  {deletingId === image.id ? (
                    <Spinner size="sm" />
                  ) : (
                    <XMarkIcon className="h-4 w-4" />
                  )}
                </button>
              </div>
              <div className="p-2 bg-white">
                <p className="text-xs text-gray-700 truncate" title={image.name}>
                  {image.name}
                </p>
                <p className="text-xs text-gray-500">
                  {formatFileSize(image.size)}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {loading && (
        <div className="text-center py-8">
          <Spinner size="lg" />
          <p className="text-sm text-gray-500 mt-2">Načítám obrázky...</p>
        </div>
      )}

      {images.length === 0 && !uploading && !loading && (
        <div className="text-center py-8">
          <PhotoIcon className="h-12 w-12 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">Zatím nejsou nahrány žádné obrázky</p>
        </div>
      )}

      {/* Image Gallery Modal */}
      <ImageGalleryModal
        images={images}
        currentIndex={currentImageIndex}
        isOpen={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        onNavigate={setCurrentImageIndex}
      />
    </div>
  );
}
